import { Component, type ErrorInfo, type ReactNode } from "react";
import { logger, serializeError } from "@/lib/logger";

interface Props {
  children: ReactNode;
  /** Compact inline fallback for a single card/panel failure. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render errors in the registration card subtree so a single
 * broken card never blanks the entire page.
 */
export class RegistrationErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error("[RegistrationErrorBoundary]", {
      ...serializeError(error),
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            Something went wrong displaying this registration. Try refreshing the page.
          </div>
        )
      );
    }
    return this.props.children;
  }
}
